import { AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToHorizontalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { Skeleton } from '@sendible/design-system';
import { EVENTS, publish } from '@sendible/event-manager';
import { reportErrorToFaro } from '@sendible/common';
import { composeTabs, MediaIdType, MediaType } from '@sendible/shared-state-bridge';
import { MediaFileType } from '@sendible/frontendv2/src/models/medias/types';
import { Container, MediaSlot, MotionButton, Slider } from './index.styles';
import { ComposeMediaCard } from './components/ComposeMediaCard';
import { SortItem } from './components/SortItem';
import { VideoSettings } from '../VideoSettings';
import { composeMediaInteractions } from '../../composeMediaInteractions';
import { useComposeBoxContext } from '../../composeBoxContext';
import { MediaPreview } from '../../..';

const prepareMediaForMediaBelt = (tabId: composeTabs) => {
  const mediaAttached = composeMediaInteractions.getAttachedMedia(tabId);
  const sorted = composeMediaInteractions.convertMediaAttachedToSortedArray(mediaAttached);

  if (sorted?.length) {
    // NOTE: If image is attached as embeded then don't display
    return sorted.filter((media) => {
      return !media.isEmbeddedImage;
    });
  }

  return sorted;
};

export const MediaBelt = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [hasSliderRef, setHasSliderRef] = useState(false);
  const [isSorting, setIsSorting] = useState(false);
  const [showPreviousButton, setShowPreviousButton] = useState(false);
  const [previewInitialItem, setPreviewInitialItem] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [focusTitleInputOnPreview, setFocusTitleInputOnPreview] = useState(false);
  const [showVideoThumbnailSettings, setShowVideoThumbnailSettings] = useState<{ isOpen: boolean; mediaObject: MediaType | null }>({
    isOpen: false,
    mediaObject: null,
  });
  const { activeComposeBoxTab } = useComposeBoxContext();
  const attachedMediasOnActiveTab = prepareMediaForMediaBelt(activeComposeBoxTab);
  const [showNextButton, setShowNextButton] = useState(!!(attachedMediasOnActiveTab && attachedMediasOnActiveTab?.length > 6));

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  const handlePrevSliderButton = () => {
    if (ref.current) {
      const { scrollLeft, children } = ref.current;
      const mediaWidth = children[0].scrollWidth;

      ref.current.scrollLeft = scrollLeft - mediaWidth;
    }
  };

  const handleNextSliderButton = () => {
    if (ref.current) {
      const { scrollLeft, children } = ref.current;
      const mediaWidth = children[0].scrollWidth;

      ref.current.scrollLeft = scrollLeft + mediaWidth;
    }
  };

  const handleOpenEditor = (mediaID: MediaIdType) => {
    const mediaObject = attachedMediasOnActiveTab ? attachedMediasOnActiveTab.find((media) => media.id === mediaID) : null;

    if (mediaObject) {
      if (mediaObject.type === MediaFileType.Video) {
        publish(EVENTS.OPEN_VIDEO_EDITOR, { media: mediaObject });
      }

      if (mediaObject.type === MediaFileType.Image) {
        publish(EVENTS.OPEN_PHOTO_EDITOR, { media: mediaObject });
      }
    }
  };

  const handleAltText = (mediaID: MediaIdType) => {
    const mediaObject = attachedMediasOnActiveTab ? attachedMediasOnActiveTab.find((media) => media.id === mediaID) : null;

    if (mediaObject) {
      publish(EVENTS.OPEN_ALT_TEXT_MODAL, mediaObject);
    }
  };

  const handleScroll = () => {
    if (!ref.current) return;

    const { offsetWidth, scrollLeft, scrollWidth } = ref.current;
    const threshold = 1;
    const isAtEnd = scrollWidth - (offsetWidth + scrollLeft) <= threshold;

    setShowPreviousButton(scrollLeft > 0);
    setShowNextButton(scrollWidth > offsetWidth && !isAtEnd);
  };

  const handleDragStart = () => window?.getSelection()?.removeAllRanges();

  const setSliderRef = useCallback((node: HTMLDivElement | null) => {
    ref.current = node;
    setHasSliderRef(Boolean(node));
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (attachedMediasOnActiveTab && over) {
      const oldIndex = attachedMediasOnActiveTab.findIndex((i) => i.id === active.id);
      const newIndex = attachedMediasOnActiveTab.findIndex((i) => i.id === over?.id);

      composeMediaInteractions.reorderMedia(oldIndex, newIndex, activeComposeBoxTab);
    }
  };

  const handlePreview = ({ media, focusTitleInput = false }: { media?: MediaType; focusTitleInput?: boolean }) => {
    setShowPreview(true);
    setFocusTitleInputOnPreview(focusTitleInput);
    setPreviewInitialItem(media ? (attachedMediasOnActiveTab?.findIndex((m) => m.id === media.id) ?? 0) : 0);
  };
  const handleDownload = (mediaId: MediaIdType) => {
    (document.activeElement as HTMLElement).blur();

    const currentMedia = attachedMediasOnActiveTab ? attachedMediasOnActiveTab.find((media) => media.id === mediaId) : null;

    if (!currentMedia) return;

    const tempButton = document.createElement('a');

    tempButton.setAttribute('download', currentMedia.name);
    tempButton.href = currentMedia.downloadUrl;
    tempButton.click();
    tempButton.remove();

    window.pendo.track('media downloaded from preview', {
      name: currentMedia.name,
      type: currentMedia.type,
    });
  };
  const handleDropdownClick = (mediaID: MediaIdType, option: string) => {
    try {
      if (option === 'remove') {
        composeMediaInteractions.removeMedia(mediaID, activeComposeBoxTab);
      }

      if (option === 'edit') {
        handleOpenEditor(mediaID);
      }

      if (option === 'alttext') {
        handleAltText(mediaID);
      }
      if (option === 'download') {
        handleDownload(mediaID);
      }
      if (option === 'settings') {
        const mediaObject = attachedMediasOnActiveTab ? attachedMediasOnActiveTab.find((media) => media.id === mediaID) : null;

        if (mediaObject) {
          setShowVideoThumbnailSettings({ isOpen: true, mediaObject });
        }
      }
      if (option === 'tag') {
        const mediaObject = attachedMediasOnActiveTab ? attachedMediasOnActiveTab.find((media) => media.id === mediaID) : null;

        if (mediaObject) {
          publish(EVENTS.OPEN_TAG_MODAL, mediaObject);
        }
      }
      if (option === 'title') {
        const mediaObject = attachedMediasOnActiveTab ? attachedMediasOnActiveTab.find((media) => media.id === mediaID) : null;

        if (mediaObject) {
          handlePreview({ media: mediaObject, focusTitleInput: true });
        }
      }
    } catch (error: unknown) {
      reportErrorToFaro(error, `MediaBelt handleDropdownClick ${option}`);
    }
  };

  useLayoutEffect(() => {
    handleScroll();
  }, [attachedMediasOnActiveTab?.length, activeComposeBoxTab]);

  useEffect(() => {
    const modalBodies = document.querySelectorAll<HTMLElement>('.modal-body');

    if (isSorting) {
      modalBodies.forEach((el) => {
        el.dataset.originalOverflow = el.style.overflowY || '';
        el.style.overflowY = 'hidden';
      });
    } else {
      modalBodies.forEach((el) => {
        el.style.overflowY = el.dataset.originalOverflow || '';
        delete el.dataset.originalOverflow;
      });
    }
  }, [isSorting]);

  const targetPortal = document.getElementById('mediaAttached');

  if (!targetPortal) return null;

  return createPortal(
    <>
      <Container>
        {/* 🛠️ Upgraded for v12: Keep AnimatePresence mounted at the root layout node
           level and use 'popLayout' so sibling absolute targets clear instantly */}
        <AnimatePresence mode="popLayout">
          {hasSliderRef && !isSorting && showPreviousButton && (
            <MotionButton
              className="attach-prev nav-button"
              icon="arrow_left"
              key="prev-button"
              onClick={handlePrevSliderButton}
              size={16}
              initial={{ x: '-5rem', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '-5rem', opacity: 0 }}
            />
          )}
          {hasSliderRef && !isSorting && showNextButton && (
            <MotionButton
              className="attach-next nav-button"
              icon="arrow_right"
              key="next-button"
              onClick={handleNextSliderButton}
              size={16}
              initial={{ x: '5rem', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '5rem', opacity: 0 }}
            />
          )}
        </AnimatePresence>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
        >
          <SortableContext
            items={attachedMediasOnActiveTab?.map((media) => media.id) || []}
            strategy={horizontalListSortingStrategy}
          >
            <Slider
              $isEmpty={!attachedMediasOnActiveTab?.length}
              $isSorting={isSorting}
              data-testid="media-belt-slider"
              onScroll={handleScroll}
              ref={setSliderRef}
            >
              {attachedMediasOnActiveTab?.map((media, index) => (
                <SortItem
                  key={media.id}
                  id={media.id}
                  onSortChange={(state) => setIsSorting(state)}
                >
                  <MediaSlot
                    key={`${activeComposeBoxTab}-${media.id}-${index}`}
                    className="media-slot"
                  >
                    {media.status != 'Ready' ? (
                      <Skeleton
                        key={`loading_${index}`}
                        shape="square"
                        width="6.5rem"
                      />
                    ) : (
                      <ComposeMediaCard
                        ariaMediaCardLabel="compose-media-label"
                        dropdownButtonClick={(option: string) => handleDropdownClick(media.id, option)}
                        isDropdownEnabled={!isSorting}
                        open={() => handlePreview({ media })}
                        orderNumber={attachedMediasOnActiveTab?.length > 1 ? `${Number(index) + 1}` : ''}
                        tabIndex={0}
                        testId="asset-video"
                        media={media}
                      />
                    )}
                  </MediaSlot>
                </SortItem>
              ))}
            </Slider>
          </SortableContext>
        </DndContext>
        {showPreview && attachedMediasOnActiveTab && (
          <MediaPreview
            autoFocusTitleInput={focusTitleInputOnPreview}
            initialMediaIndex={previewInitialItem}
            mediaList={attachedMediasOnActiveTab.map(({ originalUrl, type, id, metadata, name, downloadUrl, igUserTags, fileDisplayName }) => {
              return {
                url: originalUrl,
                type: type === MediaFileType.Document ? 'pdf' : (type.toLowerCase() as 'image' | 'video'),
                id,
                isGif: !!(metadata && metadata?.contentType?.includes('gif')),
                name,
                downloadUrl,
                igUserTags,
                fileDisplayName,
              };
            })}
            onActionClick={(action, mediaId) => handleDropdownClick(mediaId, action)}
            onClose={() => {
              setShowPreview(false);
              setFocusTitleInputOnPreview(false);
            }}
            shouldShowActions
            tabId={activeComposeBoxTab}
          />
        )}
        {showVideoThumbnailSettings.isOpen && showVideoThumbnailSettings.mediaObject && (
          <VideoSettings
            mediaId={showVideoThumbnailSettings.mediaObject.id}
            setShowVideoThumbnailSettings={setShowVideoThumbnailSettings}
          />
        )}
      </Container>
    </>,
    targetPortal
  );
};
